/**
 * Cliente da API.
 *
 * Os tipos vêm de `esquema.ts`, gerado do OpenAPI do FastAPI com
 * `npm run tipos` — nunca escritos à mão. É o que impede a divergência
 * silenciosa: a versão anterior deste arquivo descrevia um backend de
 * `rodadas` e `achados` servido por `mock.py`, com score derivado de um hash
 * do id. O TypeScript não tinha como avisar, porque os tipos também eram a
 * cópia velha.
 *
 * Hoje `rodada` é `coleta_run` de verdade, e o score vem da etapa 4, calculado
 * em Python sobre o cruzamento com os atestados — nulo apenas quando a
 * licitação não foi pontuada, que é diferente de pontuada com zero.
 */
import type { components, operations } from "./esquema";

type E = components["schemas"];

export type LicitacaoResumo = E["LicitacaoResumo"];
export type MotivoAba = E["MotivoAba"];
export type Etiqueta = E["Etiqueta"];
export type EstadoGeracao = E["EstadoGeracao"];
export type LicitacaoDetalhe = E["LicitacaoDetalhe"];
export type Facetas = E["Facetas"];
export type Contagem = E["Contagem"];
export type Progresso = E["Progresso"];
export type Acervo = E["Acervo"];
export type Rodada = E["Rodada"];
export type Fase = E["Fase"];
export type Usuario = E["Usuario"];
export type Pagina = E["Pagina"];
export type Achados = E["Achados"];
export type Decisao = E["Decisao"];
/** O parecer que uma PESSOA escreveu, para cruzar com o do sistema. */
export type AnaliseHumana = E["AnaliseHumana"];
export type Filtro = E["Filtro"];
export type OpcaoFiltro = E["OpcaoFiltro"];
export type NovaDecisao = E["NovaDecisao"];
export type Opcao = E["Opcao"];
export type Termo = E["Termo"];
export type Categoria = E["Categoria"];
export type Mudanca = E["Mudanca"];
export type Degrau = E["Degrau"];
export type JustificativaScore = E["JustificativaScore"];
export type CriterioDoScore = E["CriterioDoScore"];
export type Analise = E["Analise"];
export type LeituraDoEdital = E["LeituraDoEdital"];
export type ExigenciaCruzada = E["ExigenciaCruzada"];
export type AtestadoProximo = E["AtestadoProximo"];
export type EditalEnviado = E["EditalEnviado"];
export type Diario = E["Diario"];
export type EtapaDoJob = E["EtapaDoJob"];
/** A auditoria da manhã (`/api/auditoria`): o que tem cara de erro, na hora. */
export type Auditoria = E["Auditoria"];
/** Um achado dela — não confundir com `Achados`, o que uma varredura trouxe. */
export type AchadoDaAuditoria = E["Achado"];
export type TipoDeAchado = AchadoDaAuditoria["tipo"];
export type RespostaEnvio = E["RespostaEnvio"];
/** A ficha de um atestado do acervo, como a view `atestado_ficha` a escreve. */
export type Ficha = E["Ficha"];
export type PaginaFichas = E["PaginaFichas"];
/** Em quantas oportunidades visíveis cada atestado cruzou (`/api/atestados/uso`). */
export type UsoDoAtestado = E["UsoDoAtestado"];
export type UsoDosAtestados = E["UsoDosAtestados"];
/** Uma oportunidade visível em que o atestado está entre os pares do score. */
export type OportunidadeDoAtestado = E["OportunidadeDoAtestado"];
/** Só `atestado` comprova capacidade técnica; os outros dois estão no acervo
 * e não provam nada ao edital. */
export type Natureza = Ficha["natureza"];
/**
 * Os parâmetros de `GET /api/atestados`, tirados da rota gerada e não
 * reescritos: `busca`, `orgao`, `linha`, `natureza`, `inicio`, `quantidade`.
 */
export type FiltrosAtestados = NonNullable<
  operations["listar_api_atestados_get"]["parameters"]["query"]
>;

/** Os enums viraram uniões inline no schema; nomeá-las aqui evita repetir. */
export type Recomendacao = NonNullable<LicitacaoResumo["recomendacao"]>;
export type Nivel = NonNullable<LicitacaoResumo["nivel"]>;
export type EstadoEdital = NonNullable<LicitacaoResumo["edital"]>;
/**
 * O ESTADO da decisão numa licitação — `pendente` é valor, não ausência.
 *
 * Distinto de `Decisao` acima, que é o REGISTRO gravado (id, motivo, quem,
 * quando). Segue o mesmo par de `EstadoEdital`: o estado é o que a lista
 * mostra, o registro é o que o histórico guarda.
 */
export type EstadoDecisao = LicitacaoResumo["decisao"];
/**
 * O FIM DO FUNIL (D13): a etapa depois de aprovada, a documentação e os
 * arquivos. `EtapaDaDisputa` é o que as abas filtram.
 */
export type Disputa = E["Disputa"];
export type ItemDaDisputa = E["ItemDaDisputa"];
export type ArquivoDaDisputa = E["ArquivoDaDisputa"];
export type EtapaDaDisputa = NonNullable<Disputa["etapa"]>;
export type NovoItemDaDisputa = E["NovoItemDaDisputa"];
export type MudancaDoItem = E["MudancaDoItem"];
export type ResultadoDaDisputa = E["ResultadoDaDisputa"];
/** O atestado que uma PESSOA indica como prova de capacidade (D11). */
export type AtestadoDeclarado = E["AtestadoDeclarado"];
export type NovoAtestadoDeclarado = E["NovoAtestadoDeclarado"];

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
  ) {
    super(mensagem);
  }
}

async function pedir<T>(caminho: string, init?: RequestInit): Promise<T> {
  const r = await fetch(caminho, {
    // Sem isto o cookie de sessão não acompanha a requisição e toda chamada
    // volta 401 — com o servidor achando que ninguém entrou.
    credentials: "same-origin",
    // FormData NÃO leva Content-Type nosso: o navegador precisa gerar o
    // cabeçalho com o `boundary`, e fixar "application/json" aqui faria o
    // servidor receber um multipart que ele não sabe abrir — 422 sem pista.
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!r.ok) {
    let detalhe = r.statusText;
    try {
      const corpo = await r.json();
      // 422 do FastAPI vem como lista de erros de campo; os nossos 422 de
      // configuração vêm como string. Aceita os dois.
      // E o 409 da entrada na disputa vem como objeto, com a mensagem e o
      // número de pendentes: sem isto a tela mostraria "[object Object]".
      detalhe = Array.isArray(corpo?.detail)
        ? corpo.detail.map((d: { msg?: string }) => d.msg ?? "").join("; ")
        : typeof corpo?.detail === "object" && corpo.detail?.mensagem
          ? corpo.detail.mensagem
          : (corpo?.detail ?? detalhe);
    } catch {
      /* resposta sem corpo JSON — fica o statusText mesmo */
    }
    throw new ErroApi(r.status, detalhe);
  }
  return r.status === 204 ? (undefined as T) : ((await r.json()) as T);
}

const json = (metodo: string, corpo: unknown): RequestInit => ({
  method: metodo,
  body: JSON.stringify(corpo),
});

/** Filtros da lista. `undefined` significa "não filtra por isto". */
export interface Filtros {
  recomendacao?: string[];
  /**
   * A FAIXA DE SCORE: `avaliar` (>= score.limiar), `passar` (>= piso) ou
   * `descartar`. NÃO é `recomendacao` — aquela é a hierarquia inteira, em que
   * o edital lido ganha do parecer, que ganha do score. São duas perguntas: a
   * faixa diz onde o NÚMERO caiu, a recomendação diz o que o SISTEMA conclui.
   */
  faixa?: string[];
  uf?: string[];
  nivel?: string[];
  edital?: string[];
  termo?: string[];
  /** A linha de serviço que a triagem atribuiu. */
  categoria?: string[];
  aderencia?: string[];
  /**
   * A ETAPA DEPOIS DA DECISÃO (D13): `aprovada`, `preparacao`, `em_disputa`,
   * `vencida`, `perdida`. As três últimas ficam fora do corte de prazo no
   * servidor — a licitação em disputa já passou do prazo por natureza.
   */
  etapa?: string[];
  /** Inclui `pendente` — é como se pede a fila do dia. */
  decisao?: string[];
  /** A aba "Encontradas": só o que a última varredura trouxe. */
  encontradas?: boolean;
  /** undefined = tanto faz · false = esconde as vetadas · true = só as vetadas */
  vetadas?: boolean;
  busca?: string;
  ordem?: "score" | "local" | "prazo" | "orgao" | "recente" | "decisao";
}

export function paraConsulta(f: Filtros, extra: Record<string, string | number> = {}) {
  const p = new URLSearchParams();
  for (const [chave, valor] of Object.entries(f)) {
    if (valor === undefined || valor === "") continue;
    if (Array.isArray(valor)) valor.forEach((v) => p.append(chave, String(v)));
    else p.set(chave, String(valor));
  }
  for (const [chave, valor] of Object.entries(extra)) p.set(chave, String(valor));
  return p.toString();
}

export const api = {
  // ── acervo ────────────────────────────────────────────────────────────
  listar: (f: Filtros, inicio = 0, quantidade = 200) =>
    pedir<Pagina>(`/api/licitacoes?${paraConsulta(f, { inicio, quantidade })}`),
  detalhe: (id: string) => pedir<LicitacaoDetalhe>(`/api/licitacoes/${id}`),
  /**
   * A abertura do score: os cinco termos e os atestados que os sustentam.
   *
   * Rota separada do detalhe, e buscada só quando alguém abre a seção. O
   * detalhe é pedido a cada navegação e já é a consulta mais cara do backend;
   * isto custa uma segunda consulta e só interessa a quem quer entender o
   * número.
   *
   * 404 é resposta VÁLIDA aqui: significa "esta licitação não foi pontuada",
   * que não é o mesmo que score zero. Quem chama trata como ausência, não
   * como erro.
   */
  scoreDe: (id: string) =>
    pedir<JustificativaScore>(`/api/licitacoes/${id}/score`),
  /**
   * A leitura do edital inteiro, nas doze seções do formato da Nectar.
   *
   * 404 é resposta VÁLIDA e frequente: quer dizer "ninguém rodou
   * `nectuno-analise` nesta licitação". Não é o mesmo que "leram o edital e
   * não acharam nada", e a tela mostra coisas diferentes para os dois — a
   * análise custa uma leitura de até 180 mil caracteres e não roda sozinha
   * sobre o acervo inteiro.
   */
  analiseDe: (id: string) => pedir<Analise>(`/api/licitacoes/${id}/analise`),
  /**
   * A LEITURA INTEGRAL do edital, com o certificado e a matriz com os
   * atestados (Fases 2 e 3 da D10). EM SOMBRA: nada dela decide aba, score ou
   * recomendação — é medição, e a tela diz isso.
   *
   * 404 é resposta VÁLIDA e, por ora, a mais comum: ninguém rodou
   * `nectuno-leitura` nesta licitação. Não diz nada sobre o edital.
   */
  leituraDe: (id: string) =>
    pedir<LeituraDoEdital>(`/api/licitacoes/${id}/leitura`),
  /**
   * Dispara a leitura do edital. 202 e o processo segue sozinho.
   *
   * É a única chamada do cliente que custa dinheiro de verdade — cerca de
   * US$ 1,40, contra US$ 0,0088 da triagem. O servidor recusa com 422 quando
   * ninguém aprovou a licitação ainda, e a mensagem dele é o que a tela
   * mostra: a guarda mora lá, não aqui.
   */
  /** A geracao pedida pelo botao: pronta, rodando, falhou (com motivo) ou nunca. */
  estadoGeracao: (id: string) =>
    pedir<EstadoGeracao>(`/api/licitacoes/${id}/analise/estado`),
  pedirAnalise: (id: string) =>
    pedir<{ licitacao_id: number; estado: string }>(
      `/api/licitacoes/${id}/analise`,
      { method: "POST" },
    ),
  facetas: () => pedir<Facetas>("/api/facetas"),

  // ── o job diário ──────────────────────────────────────────────────────
  diario: () => pedir<Diario>("/api/diario"),
  /**
   * Dispara a execução à mão. 202, não 200: o trabalho começou e não terminou.
   *
   * A resposta traz o estado logo após o disparo — a rodada leva de dez a
   * trinta minutos, e quem chama precisa continuar consultando `diario()`.
   */
  rodarDiario: () => pedir<Diario>("/api/diario", { method: "POST" }),
  pararDiario: () => pedir<void>("/api/diario/parar", { method: "POST" }),
  /**
   * A auditoria da manhã: a travada em 69, o NO-GO em Avaliar, a duplicada, a
   * leitura parada e a noite curta. Calculada na hora — o que foi consertado
   * sai sozinho. Só lê.
   */
  auditoria: () => pedir<Auditoria>("/api/auditoria"),

  // ── edital trazido à mão ──────────────────────────────────────────────
  editais: () => pedir<EditalEnviado[]>("/api/editais"),
  /**
   * Manda um edital e recebe a licitação analisada.
   *
   * DEMORA dezenas de segundos: a rota é síncrona porque o pedido é "mandar e
   * receber a recomendação", e um 202 com polling exigiria uma tela de espera
   * que ninguém pediu. Quem chama precisa mostrar que está trabalhando.
   *
   * `ja_existia` verdadeiro quer dizer que um edital de texto idêntico já
   * tinha sido analisado — nada foi recalculado e nada foi cobrado.
   */
  enviarEdital: (arquivo: File) => {
    const corpo = new FormData();
    corpo.append("arquivo", arquivo);
    return pedir<RespostaEnvio>("/api/editais", { method: "POST", body: corpo });
  },
  acervo: () => pedir<Acervo>("/api/acervo"),
  funil: () => pedir<Degrau[]>("/api/funil"),

  // ── o acervo de ATESTADOS (o que o Nectar já executou) ────────────────
  /**
   * As fichas dos atestados, na ordem do servidor (data crescente, sem data no
   * fim). `busca` procura em título, atividade e órgão; `linha` e `natureza`
   * são exatos. Vazio, nulo ou ausente é "não filtra por isto".
   *
   * `quantidade` vai até 1000 e o acervo tem ~201: quem quer tudo pede tudo,
   * numa página só.
   */
  atestados: (f: FiltrosAtestados = {}) => {
    const p = new URLSearchParams();
    for (const [chave, valor] of Object.entries(f)) {
      if (valor === undefined || valor === null || valor === "") continue;
      p.set(chave, String(valor));
    }
    const q = p.toString();
    return pedir<PaginaFichas>(`/api/atestados${q ? `?${q}` : ""}`);
  },
  /**
   * A ficha de um atestado pelo código. Vai SEM o `#`: no caminho ele teria
   * de ser `%23`, e a rota aceita `199` e `#199` do mesmo jeito.
   */
  atestado: (codigo: string) =>
    pedir<Ficha>(`/api/atestados/${encodeURIComponent(codigo.replace(/^#/, ""))}`),
  /**
   * Em quantas oportunidades cada atestado cruzou, contando SÓ as que as abas
   * mostram (o funil e a Regra 1): quem não cruzou com nenhuma não vem.
   */
  usoDosAtestados: () => pedir<UsoDosAtestados>("/api/atestados/uso"),
  /** As oportunidades visíveis em que o atestado está entre os pares. */
  oportunidadesDoAtestado: (codigo: string) =>
    pedir<OportunidadeDoAtestado[]>(
      `/api/atestados/${encodeURIComponent(codigo.replace(/^#/, ""))}/oportunidades`,
    ),

  // ── decisão humana ────────────────────────────────────────────────────
  decidir: (id: string, d: NovaDecisao) =>
    pedir<Decisao>(`/api/licitacoes/${id}/decisao`, json("POST", d)),
  decisoesDe: (id: string) => pedir<Decisao[]>(`/api/licitacoes/${id}/decisao`),
  decisoes: (limite = 100) => pedir<Decisao[]>(`/api/decisoes?limite=${limite}`),

  // ── o fim do funil: documentação, disputa e resultado (D13) ───────────
  // Toda escrita devolve o estado inteiro: a tela não precisa buscar de novo
  // para saber quantos itens ficaram pendentes.
  disputa: (id: string) => pedir<Disputa>(`/api/licitacoes/${id}/disputa`),
  iniciarPreparacao: (id: string) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/preparacao`, { method: "POST" }),
  /** Acrescenta o que a pré-análise passou a sugerir depois do início. */
  sugerirItensDaDisputa: (id: string) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/sugestoes`, { method: "POST" }),
  novoItemDaDisputa: (id: string, d: NovoItemDaDisputa) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/itens`, json("POST", d)),
  mudarItemDaDisputa: (id: string, item: number, d: MudancaDoItem) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/itens/${item}`, json("PATCH", d)),
  removerItemDaDisputa: (id: string, item: number) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/itens/${item}`, { method: "DELETE" }),
  /** FormData, como o edital enviado: é o navegador que gera o `boundary`. */
  anexarNaDisputa: (id: string, item: number, arquivo: File) => {
    const corpo = new FormData();
    corpo.append("arquivo", arquivo);
    return pedir<Disputa>(`/api/licitacoes/${id}/disputa/itens/${item}/arquivos`, {
      method: "POST",
      body: corpo,
    });
  },
  /** O endereço de baixar: a rota responde como anexo, e o cookie da sessão
   *  acompanha por ser a mesma origem. */
  urlDoArquivoDaDisputa: (id: string, arquivo: number) =>
    `/api/licitacoes/${id}/disputa/arquivos/${arquivo}`,
  /** Apaga o conteúdo do arquivo; o registro de quem enviou e removeu fica. */
  removerArquivoDaDisputa: (id: string, arquivo: number) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/arquivos/${arquivo}`,
      { method: "DELETE" }),
  /** `ciente`: a tela já mostrou quantos itens estão pendentes e a pessoa
   *  confirmou. Sem ele, com pendentes, o servidor responde 409. */
  entrarEmDisputa: (id: string, ciente: boolean) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/entrada`,
      json("POST", { ciente_dos_pendentes: ciente })),
  informarResultado: (id: string, d: ResultadoDaDisputa) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/resultado`, json("POST", d)),
  /** Volta um passo: o resultado, a entrada na disputa ou o início da
   *  preparação. O que foi preparado fica guardado. */
  desfazerNaDisputa: (id: string) =>
    pedir<Disputa>(`/api/licitacoes/${id}/disputa/desfaz`, { method: "POST" }),

  // ── o atestado que a gestão indica (D11) ──────────────────────────────
  /** Os atestados indicados por uma pessoa como prova de capacidade. Contam
   *  como cobertura: por inteiro na matriz, afinidade máxima pelo objeto. */
  atestadosDeclarados: (id: string) =>
    pedir<AtestadoDeclarado[]>(`/api/licitacoes/${id}/atestados-declarados`),
  declararAtestado: (id: string, d: NovoAtestadoDeclarado) =>
    pedir<AtestadoDeclarado>(`/api/licitacoes/${id}/atestados-declarados`,
      json("POST", d)),
  /** Retira a indicação. A linha fica no banco, com quem retirou. */
  retirarAtestado: (id: string, declaracao: number) =>
    pedir<void>(`/api/licitacoes/${id}/atestados-declarados/${declaracao}`,
      { method: "DELETE" }),

  // ── a análise que a PESSOA escreveu ───────────────────────────────────
  /**
   * Manda um parecer humano sobre a licitação, para cruzar com o do sistema.
   *
   * O `veredito` é a frase curta que a lista mostra sem abrir o texto inteiro;
   * quem já escreveu tudo no documento pode deixar em branco.
   *
   * FormData, e não JSON: o backend recebe `UploadFile`, e é o navegador que
   * precisa gerar o `boundary` do multipart — ver o comentário de `pedir`.
   */
  enviarAnaliseHumana: (id: string, arquivo: File, veredito?: string) => {
    const corpo = new FormData();
    corpo.append("arquivo", arquivo);
    if (veredito?.trim()) corpo.append("veredito", veredito.trim());
    return pedir<AnaliseHumana>(`/api/licitacoes/${id}/analise-humana`, {
      method: "POST",
      body: corpo,
    });
  },
  analisesHumanas: (id: string) =>
    pedir<AnaliseHumana[]>(`/api/licitacoes/${id}/analise-humana`),

  // ── coleta (as "rodadas" das telas) ───────────────────────────────────
  fases: () => pedir<Fase[]>("/api/fases"),
  /** As fontes que o runner sabe executar — vem de `FONTES`, não de uma cópia. */
  fontes: () => pedir<string[]>("/api/fontes"),
  /**
   * Os filtros da pesquisa avançada de uma fonte, lidos do formulário dela.
   *
   * Lista vazia quer dizer "esta fonte não tem pesquisa avançada" — hoje o
   * PNCP. Nunca uma cópia aqui: os ids das áreas do Licitmais são do perfil
   * da conta e mudam com o contrato.
   */
  filtrosDaFonte: (fonte: string) =>
    pedir<Filtro[]>(`/api/fontes/${encodeURIComponent(fonte)}/filtros`),
  rodadas: (limite = 30) => pedir<Rodada[]>(`/api/rodadas?limite=${limite}`),
  rodadaAtual: () => pedir<Progresso | null>("/api/rodadas/atual"),
  estadoRodada: (id: string) => pedir<Progresso>(`/api/rodadas/${id}`),
  /** O que a coleta já trouxe — consultável com ela ainda correndo. */
  achados: (id: string) => pedir<Achados>(`/api/rodadas/${id}/achados`),
  /**
   * Dispara uma coleta. `termo` é o nome da fonte — vazio roda todas.
   *
   * `seco` não escreve licitação nenhuma (o run ainda é aberto e fechado);
   * `retoma` continua o último run `parcial` da fonte. Os dois juntos são
   * recusados com 422 pelo servidor: a passada seca fecharia como vazio um run
   * parcial de verdade.
   */
  iniciarRodada: (
    termo: string,
    opcoes: {
      seco?: boolean;
      retoma?: boolean;
      limite?: number;
      /** Sondagem por uma palavra só, no lugar do vocabulário da tabela. */
      palavra?: string;
      /**
       * Filtros da pesquisa avançada, com os nomes de campo da fonte
       * (`idestado[]`, e não um apelido nosso).
       */
      filtros?: Record<string, string | string[]>;
    } = {},
  ) => pedir<Progresso>("/api/rodadas", json("POST", { termo, ...opcoes })),

  /**
   * Pede a parada de uma coleta em curso.
   *
   * Devolve o progresso com `cancelando: true` e o estado ainda `rodando` — o
   * pedido foi registrado, e quem encerra a linha é o runner, no próximo
   * checkpoint de página. 409 quando a coleta já terminou sozinha entre o
   * clique e a requisição.
   */
  pararRodada: (id: string) =>
    pedir<Progresso>(`/api/rodadas/${encodeURIComponent(id)}/parar`, {
      method: "POST",
    }),

  // ── configuração ──────────────────────────────────────────────────────
  config: () => pedir<Opcao[]>("/api/config"),
  mudaConfig: (chave: string, valor: unknown) =>
    pedir<Opcao>(`/api/config/${encodeURIComponent(chave)}`, json("PUT", { valor })),
  termos: (estado?: string) =>
    pedir<Termo[]>(`/api/termos${estado ? `?estado=${encodeURIComponent(estado)}` : ""}`),
  mudaTermo: (termo: string, estado: Termo["estado"], nota?: string | null) =>
    pedir<void>(
      `/api/termos/${encodeURIComponent(termo)}`,
      json("PUT", { estado, nota: nota ?? null }),
    ),
  /** Exclui o termo de vez: o sistema deixa de buscar por ele. */
  apagaTermo: (termo: string) =>
    pedir<void>(`/api/termos/${encodeURIComponent(termo)}`, { method: "DELETE" }),
  categorias: () => pedir<Categoria[]>("/api/categorias"),
  mudaCategoria: (c: Categoria) =>
    pedir<void>(`/api/categorias/${encodeURIComponent(c.nome)}`, json("PUT", c)),
  historico: (limite = 40) => pedir<Mudanca[]>(`/api/historico?limite=${limite}`),

  // ── sessão ────────────────────────────────────────────────────────────
  eu: () => pedir<Usuario>("/auth/eu"),
  entrar: (email: string, senha: string) =>
    pedir<Usuario>("/auth/entrar", json("POST", { email, senha })),
  sair: () => pedir<void>("/auth/sair", { method: "POST" }),
  /** A senha esquecida (0143): pede o link por e-mail. A resposta é a mesma
   *  para e-mail que existe e que não existe; 503 é o envio desligado. */
  esqueci: (email: string) => pedir<void>("/auth/esqueci", json("POST", { email })),
  /** Troca a senha com o token do link. 400: o link não vale mais. */
  redefinir: (token: string, senha: string) =>
    pedir<void>("/auth/redefinir", json("POST", { token, senha })),
};

/**
 * Acompanha uma coleta por Server-Sent Events.
 *
 * Substitui o polling de 400 ms da versão anterior, que além de barulhento
 * nunca era cancelado: sair da tela deixava o laço rodando, e cada recarga
 * da página deixava mais um para sempre. Aqui `fechar()` encerra de fato.
 */
export function acompanhar(
  rodadaId: string,
  aoEvento: (p: Progresso) => void,
  aoFim?: (p: Progresso | null) => void,
): () => void {
  const fonte = new EventSource(`/api/rodadas/${rodadaId}/eventos`);
  let ultimo: Progresso | null = null;

  fonte.onmessage = (e) => {
    const p = JSON.parse(e.data) as Progresso;
    ultimo = p;
    aoEvento(p);
    if (p.estado === "concluida" || p.estado === "falhou") {
      fonte.close();
      aoFim?.(p);
    }
  };
  fonte.onerror = () => {
    fonte.close();
    aoFim?.(ultimo);
  };
  return () => fonte.close();
}
